import { queryOptions } from '@tanstack/react-query';

import type { Tables } from '@archetelos/db';

import { CONTENT_NO_BODY_FIELDS } from '@/lib/queries/content';
import { supabase } from '@/lib/supabase';

export type CreatorApplication = Tables<'creator_applications'>;
export type Content = Tables<'content'>;
export type Correction = Tables<'corrections'>;
export type Profile = Tables<'profiles'>;
export type InviteCode = Tables<'invite_codes'>;
export type WaitlistEntry = Tables<'waitlist'>;
export type MastheadEntry = Tables<'masthead_entries'>;
export type AuditEntry = Tables<'audit_log'>;
export type ContentReview = Tables<'content_reviews'>;

// --- audit + email helpers -------------------------------------------------

/**
 * Record an admin action. audit_log insert is service-role only, so this goes
 * through the log_admin_action SECURITY DEFINER RPC (asserts is_admin, stamps
 * actor_id = auth.uid()). Best-effort: never let an audit failure block the
 * primary mutation.
 */
export async function auditLog(
  action: string,
  targetTable: string | null,
  targetId: string | null,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_admin_action', {
      p_action: action,
      p_target_table: targetTable ?? undefined,
      p_target_id: targetId ?? undefined,
      p_metadata: metadata as never,
    });
    if (error) console.error('auditLog failed (non-fatal)', error);
  } catch (e) {
    console.error('auditLog threw (non-fatal)', e);
  }
}

/**
 * Fire a transactional email via the send-notification Edge Function. The
 * browser cannot send email directly. Best-effort: an email failure must never
 * roll back the admin action that triggered it.
 */
export async function notify(
  template: string,
  to: string | null,
  props: Record<string, unknown>,
): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('send-notification', {
      body: { template, to, props },
    });
    if (error) console.error(`notify(${template}) failed (non-fatal)`, error);
  } catch (e) {
    console.error(`notify(${template}) threw (non-fatal)`, e);
  }
}

// --- queries ---------------------------------------------------------------

export const adminQueries = {
  applications: (status: string | null) =>
    queryOptions({
      queryKey: ['admin', 'applications', status] as const,
      queryFn: async () => {
        let q = supabase
          .from('creator_applications')
          .select('*, profile:user_id (display_name, username, avatar_url)')
          .order('created_at', { ascending: false });
        if (status) q = q.eq('status', status);
        const { data, error } = await q;
        if (error) throw error;
        return data as unknown as Array<
          CreatorApplication & {
            profile: {
              display_name: string | null;
              username: string | null;
              avatar_url: string | null;
            } | null;
          }
        >;
      },
    }),

  contentQueue: (status: string | null) =>
    queryOptions({
      queryKey: ['admin', 'content', status] as const,
      queryFn: async () => {
        let q = supabase
          .from('content')
          .select(
            `${CONTENT_NO_BODY_FIELDS}, creator:creator_id (display_name, username, avatar_url, creator_status)`,
          )
          .order('updated_at', { ascending: true });
        if (status) q = q.eq('status', status);
        const { data, error } = await q;
        if (error) throw error;
        return data as unknown as Array<
          Content & {
            creator: {
              display_name: string | null;
              username: string | null;
              avatar_url: string | null;
              creator_status: string;
            } | null;
          }
        >;
      },
    }),

  contentReviews: (contentId: string | undefined) =>
    queryOptions({
      queryKey: ['admin', 'content-reviews', contentId] as const,
      queryFn: async (): Promise<ContentReview[]> => {
        if (!contentId) return [];
        const { data, error } = await supabase
          .from('content_reviews')
          .select('*')
          .eq('content_id', contentId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data ?? []) as ContentReview[];
      },
      enabled: Boolean(contentId),
    }),

  creatorApprovalRate: (creatorId: string | undefined) =>
    queryOptions({
      queryKey: ['admin', 'approval-rate', creatorId] as const,
      queryFn: async (): Promise<{ published: number; total: number }> => {
        if (!creatorId) return { published: 0, total: 0 };
        const { data, error } = await supabase
          .from('content')
          .select('status')
          .eq('creator_id', creatorId);
        if (error) throw error;
        const rows = data ?? [];
        const decided = rows.filter((r) => ['published', 'rejected'].includes(r.status as string));
        const published = rows.filter((r) => r.status === 'published').length;
        return { published, total: decided.length };
      },
      enabled: Boolean(creatorId),
    }),

  picks: () =>
    queryOptions({
      queryKey: ['admin', 'picks'] as const,
      queryFn: async () => {
        const { data, error } = await supabase
          .from('content')
          .select(`${CONTENT_NO_BODY_FIELDS}, creator:creator_id (display_name, username)`)
          .eq('is_editors_pick', true)
          .eq('status', 'published')
          .order('editors_pick_at', { ascending: false });
        if (error) throw error;
        return data as unknown as Array<
          Content & { creator: { display_name: string | null; username: string | null } | null }
        >;
      },
    }),

  corrections: (search: string) =>
    queryOptions({
      queryKey: ['admin', 'corrections', search] as const,
      queryFn: async () => {
        const { data, error } = await supabase
          .from('corrections')
          .select('*, content:content_id (title, slug, creator_id)')
          .order('created_at', { ascending: false });
        if (error) throw error;
        const rows = (data ?? []) as unknown as Array<
          Correction & {
            content: { title: string; slug: string; creator_id: string } | null;
          }
        >;
        const q = search.trim().toLowerCase();
        return q ? rows.filter((r) => r.content?.title.toLowerCase().includes(q)) : rows;
      },
    }),

  publishedContentLookup: (search: string) =>
    queryOptions({
      queryKey: ['admin', 'content-lookup', search] as const,
      queryFn: async () => {
        const q = search.trim();
        if (q.length < 2) return [];
        const { data, error } = await supabase
          .from('content')
          .select('id, title, slug, creator_id')
          .eq('status', 'published')
          .ilike('title', `%${q}%`)
          .limit(10);
        if (error) throw error;
        return (data ?? []) as unknown as Array<{
          id: string;
          title: string;
          slug: string;
          creator_id: string;
        }>;
      },
      enabled: search.trim().length >= 2,
    }),

  users: (filter: string, search: string) =>
    queryOptions({
      queryKey: ['admin', 'users', filter, search] as const,
      queryFn: async () => {
        let q = supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200);
        if (filter === 'subscribers') q = q.eq('role', 'subscriber');
        else if (filter === 'creators') q = q.eq('role', 'creator');
        else if (filter === 'admins') q = q.eq('role', 'admin');
        else if (filter === 'moderators') q = q.eq('role', 'moderator');
        else if (filter === 'suspended') q = q.not('suspended_at', 'is', null);
        const { data, error } = await q;
        if (error) throw error;
        const rows = (data ?? []) as Profile[];
        const s = search.trim().toLowerCase();
        return s
          ? rows.filter(
              (r) =>
                r.username?.toLowerCase().includes(s) || r.display_name?.toLowerCase().includes(s),
            )
          : rows;
      },
    }),

  userAudit: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['admin', 'user-audit', userId] as const,
      queryFn: async (): Promise<AuditEntry[]> => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('audit_log')
          .select('*')
          .eq('actor_id', userId)
          .order('created_at', { ascending: false })
          .limit(10);
        if (error) throw error;
        return (data ?? []) as AuditEntry[];
      },
      enabled: Boolean(userId),
    }),

  audit: (page: number) =>
    queryOptions({
      queryKey: ['admin', 'audit', page] as const,
      queryFn: async () => {
        const from = page * 50;
        const { data, error } = await supabase
          .from('audit_log')
          .select('*, actor:actor_id (display_name, username)', { count: 'exact' })
          .order('created_at', { ascending: false })
          .range(from, from + 49);
        if (error) throw error;
        return (data ?? []) as unknown as Array<
          AuditEntry & {
            actor: { display_name: string | null; username: string | null } | null;
          }
        >;
      },
    }),

  masthead: () =>
    queryOptions({
      queryKey: ['admin', 'masthead'] as const,
      queryFn: async (): Promise<MastheadEntry[]> => {
        const { data, error } = await supabase
          .from('masthead_entries')
          .select('*')
          .order('role_group', { ascending: true })
          .order('display_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as MastheadEntry[];
      },
    }),

  invites: (status: string) =>
    queryOptions({
      queryKey: ['admin', 'invites', status] as const,
      queryFn: async () => {
        const { data, error } = await supabase
          .from('invite_codes')
          .select('*, used:used_by (display_name, username)')
          .order('created_at', { ascending: false });
        if (error) throw error;
        let rows = (data ?? []) as unknown as Array<
          InviteCode & {
            used: { display_name: string | null; username: string | null } | null;
          }
        >;
        const now = Date.now();
        if (status === 'unused')
          rows = rows.filter(
            (r) => !r.used_at && (!r.expires_at || new Date(r.expires_at).getTime() > now),
          );
        else if (status === 'used') rows = rows.filter((r) => Boolean(r.used_at));
        else if (status === 'expired')
          rows = rows.filter(
            (r) => !r.used_at && r.expires_at && new Date(r.expires_at).getTime() <= now,
          );
        return rows;
      },
    }),

  waitlist: (status: string) =>
    queryOptions({
      queryKey: ['admin', 'waitlist', status] as const,
      queryFn: async (): Promise<WaitlistEntry[]> => {
        let q = supabase.from('waitlist').select('*').order('created_at', { ascending: false });
        if (status) q = q.eq('status', status);
        const { data, error } = await q;
        if (error) throw error;
        return (data ?? []) as WaitlistEntry[];
      },
    }),

  dashboardStats: () =>
    queryOptions({
      queryKey: ['admin', 'dashboard-stats'] as const,
      queryFn: async () => {
        const [apps, content, waitlist, subs] = await Promise.all([
          supabase
            .from('creator_applications')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending'),
          supabase
            .from('content')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending_review'),
          supabase
            .from('waitlist')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending'),
          supabase
            .from('subscriptions')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'active'),
        ]);
        return {
          pendingApplications: apps.count ?? 0,
          pendingContent: content.count ?? 0,
          waitlistPending: waitlist.count ?? 0,
          activeSubscribers: subs.count ?? 0,
        };
      },
    }),
};
